import './FeedEditPage.css'
import { BasicPage } from '@/components/layout/BasicPage'
import { useParams, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { feedGetSpec, feedSaveSpec, feedDeleteSpec, feedNewPasscodeSpec, tenantGetSpec, type FeedRow, type FeedSchedule, UserRole, TenantUserRole, feedCullSpec } from 'aevocam-rest-api-spec'
import {
  timeSlotsToStr,
  strToTimeSlots,
  remoteAddressRangesToStr,
  strToRemoteAddressRanges,
} from './FeedEditPage_helpers'
import { useApi } from '@/hooks/useApi'
import { Button } from '@/components/action/Button'
import { Notice } from '@/components/action/Notice'
import { TextBox } from '@/components/scalar/TextBox'
import { SelectBox } from '@/components/scalar/SelectBox'
import { routes } from '@/utilities/routes'
import { toastError, toastSuccess, toastWarning } from '@/components/action/Toaster'
import { TextArea } from '@/components/scalar/TextArea'
import { TimezoneBox } from '@/components/scalar/TimezoneBox'
import { FeedEditSchedule } from './FeedEditSchedule'
import { FeedEditPhotoSource } from './FeedEditPhotoSource'
import { TimeSlots } from './TimeSlots'
import { formatDecimal } from '@/utilities/formatters'

export function FeedEditPage() {
  const { tenant: tenantId, feed: feedId } = useParams<{ tenant: string; feed: string }>()

  const api = useApi({ requireLogin: true })
  const navigate = useNavigate()

  const userRole = api.status.session?.userRole
  const tenantUserRole = api.status.session?.tenantUserRole
  const canEdit =
    userRole === UserRole.Admin ||
    tenantUserRole === TenantUserRole.Owner ||
    tenantUserRole === TenantUserRole.Admin

  const [feed, setFeed] = useState<FeedRow | null>(null)
  const [loading, setLoading] = useState(true)

  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState('')
  const [imgHeight, setImgHeight] = useState('')
  const [flip, setFlip] = useState<FeedRow['flip']>(undefined)
  const [rotate, setRotate] = useState<FeedRow['rotate']>(undefined)
  const [timeSlotEarly, setTimeSlotEarly] = useState('')
  const [timeSlotLate, setTimeSlotLate] = useState('')
  const [timeSlots, setTimeSlots] = useState('')
  const [remoteAddressRanges, setRemoteAddressRanges] = useState('')
  const [notes, setNotes] = useState('')
  const [schedule, setSchedule] = useState<FeedSchedule | undefined>(undefined)
  const [scheduleDirty, setScheduleDirty] = useState(false)
  const [generatedPasscode, setGeneratedPasscode] = useState<string | null>(null)
  const [generatingPasscode, setGeneratingPasscode] = useState(false)
  const [busyCulling, setBusyCulling] = useState(false)
  const [photosCulled, setPhotosCulled] = useState(-1)
  const [photosRemaining, setPhotosRemaining] = useState(-1)

  //--------------------------------------------------------------------------------
  const ingestSlotsForValidation = useMemo(
    () => strToTimeSlots(timeSlots),
    [timeSlots],
  )

  //--------------------------------------------------------------------------------
  const setScheduleFromEditor = useCallback((value: FeedSchedule | undefined) => {
    const simulateChanged = value?.simulate !== schedule?.simulate
    setSchedule(value)
    if (simulateChanged && !value?.simulate) {
      toastWarning(<>
        <p>
          Sure you meant to turn off simulation mode?
        </p>
        <p>
          You may lose photos when you save!
        </p>
      </>)

    } else if (!simulateChanged && !value?.simulate && !!value?.cull?.length) {
      toastWarning(<>
        <p>
          Switching on culling simulation mode for now.
        </p>
        <p>
          I recommend keeping simulation on, saving, and testing
          the results first. Then come back here and turn it off 
          once you know the results are what you want.
        </p>
      </>)
      setSchedule(prev => ({
        ...prev,
        simulate: true,
      }))
    }
    if (!scheduleDirty) {
      setScheduleDirty(true)
    }
  }, [schedule, scheduleDirty])

  //--------------------------------------------------------------------------------
  const loadFeed = useCallback((value: FeedRow) => {
    setFeed(value)
    setName(value.name ?? '')
    setTimezone(value.timezone ?? 'America/New_York')
    setImgHeight(value.imgHeight != null ? String(value.imgHeight) : '')
    setFlip(value.flip ?? undefined)
    setRotate(value.rotate ?? undefined)
    setTimeSlotEarly(value.timeSlotEarly != null ? String(value.timeSlotEarly) : '')
    setTimeSlotLate(value.timeSlotLate != null ? String(value.timeSlotLate) : '')
    setTimeSlots(timeSlotsToStr(value.timeSlots))
    setRemoteAddressRanges(remoteAddressRangesToStr(value.remoteAddressRanges))
    setNotes(value.notes ?? '')
    setSchedule(value.schedule as FeedSchedule ?? undefined)
    setScheduleDirty(false)
  }, [])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    async function fn() {
      if (!tenantId || !feedId) return
      if (feedId === 'new') {

        let defaultTimezone = 'America/New_York'
        try {
          const tenantRes = await api.request(tenantGetSpec, {
            path: { tenant: tenantId },
          })
          if (tenantRes.status === 200 && tenantRes.data?.tenant?.timezone) {
            defaultTimezone = tenantRes.data.tenant.timezone
          }
        } catch {
          // keep default
        }

        loadFeed({
          name: 'Some camera name',
          timezone: defaultTimezone,
          disabled: false,
          imgHeight: 1080,
          flip: undefined,
          rotate: undefined,
          timeSlotEarly: 30,
          timeSlotLate: undefined,
          timeSlots: [],
          remoteAddressRanges: null,
          notes: null,
          schedule: undefined,
        })
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const res = await api.request(feedGetSpec, {
          path: { tenant: tenantId, feed: feedId },
        })
        if (res.status === 200 && res.data?.feed) {
          loadFeed(res.data.feed)
        } else {
          toastError(res.error ?? 'Failed to load feed')
        }
      } catch {
        toastError('Failed to load feed')
      } finally {
        setLoading(false)
      }
    }
    void fn()
  }, [tenantId, feedId, loadFeed, api])

  //--------------------------------------------------------------------------------
  async function handleSave(action?: 'enable' | 'disable') {
    if (!tenantId || !feedId || !feed) return

    const disabled =
      action === 'enable' ? false : action === 'disable' ? true : (feed.disabled ?? false)

    const payload: FeedRow = {
      ...feed,
      name: name || undefined,
      timezone: timezone || undefined,
      disabled,
      imgHeight: imgHeight === '' ? undefined : parseInt(imgHeight, 10),
      flip: flip ?? undefined,
      rotate: rotate ?? undefined,
      timeSlotEarly: timeSlotEarly === '' ? undefined : parseInt(timeSlotEarly, 10),
      timeSlotLate: timeSlotLate === '' ? undefined : parseInt(timeSlotLate, 10),
      timeSlots: strToTimeSlots(timeSlots),
      remoteAddressRanges: strToRemoteAddressRanges(remoteAddressRanges),
      notes: notes || null,
      // Omit schedule unless edited so PATCH-style saves do not clear it; null clears when dirty.
      schedule: scheduleDirty ? (schedule ?? null) : undefined,
    }

    const res = await api.request(feedSaveSpec, {
      path: { tenant: tenantId, feed: feedId },
      body: { feed: payload },
    })

    if (res.status === 200 && res.data?.feed) {

      setScheduleDirty(false)

      const savedFeed = res.data.feed
      setFeed(savedFeed)
      loadFeed(savedFeed)

      const passcodeText = (savedFeed as { passcodeText?: string }).passcodeText
      if (passcodeText)
        setGeneratedPasscode(passcodeText)

      if (feedId === 'new') {
        toastSuccess('Feed created')
        navigate(routes.feedEdit(tenantId, String(savedFeed.id)))
      } else {
        toastSuccess('Saved')
      }

      setPhotosCulled(-1)
      setPhotosRemaining(-1)
      if (scheduleDirty) {
        setBusyCulling(true)
        try {
          const cullFeedId = feedId === 'new' ? String(savedFeed.id) : feedId
          const res2 = await api.request(feedCullSpec, {
            path: { tenant: tenantId, feed: cullFeedId },
          })
          if (res2.status === 200) {
            setPhotosCulled(res2.data?.photosCulled ?? -1)
            setPhotosRemaining(res2.data?.photosRemaining ?? -1)
            toastSuccess(`Culling schedule applied with ${formatDecimal(res2.data?.photosCulled)} photos culled`)
          } else {
            console.error(res2.status, res2.error)
            toastError(res2.error ?? 'Error applying culling schedule')
          }
        } catch (err) {
          console.error(err)
          toastError('Error applying culling schedule')
        }
        setBusyCulling(false)
      }

    } else {
      toastError(res.error ?? 'Failed to save feed')
    }
  }

  //--------------------------------------------------------------------------------
  const handleDelete = useCallback(async () => {
    if (!tenantId || !feedId || feedId === 'new' || !feed) return
    if (!confirm('Delete this feed?')) return

    const res = await api.request(feedDeleteSpec, {
      path: { tenant: tenantId, feed: feedId },
    })
    if (res.status === 200) {
      toastWarning('Feed deleted')
      navigate(routes.home(tenantId))
    } else {
      toastError(res.error ?? 'Failed to delete')
    }
  }, [tenantId, feedId, feed, api, navigate])

  //--------------------------------------------------------------------------------
  const handleGeneratePasscode = useCallback(async () => {
    if (!tenantId || !feedId || feedId === 'new') return
    if (!confirm('Generate a new passcode? The current passcode will stop working.')) return
    setGeneratingPasscode(true)
    setGeneratedPasscode(null)
    try {
      const res = await api.request(feedNewPasscodeSpec, {
        path: { tenant: tenantId, feed: feedId },
      })
      if (res.status === 200 && res.data?.passcodeText) {
        setGeneratedPasscode(res.data.passcodeText)
        toastSuccess('New passcode generated')
      } else {
        toastError(res.error ?? 'Failed to generate passcode')
      }
    } catch {
      toastError('Failed to generate passcode')
    } finally {
      setGeneratingPasscode(false)
    }
  }, [tenantId, feedId, api])

  //--------------------------------------------------------------------------------
  const handleCopyPasscode = useCallback(() => {
    if (generatedPasscode == null) return
    void navigator.clipboard.writeText(generatedPasscode).then(
      () => toastSuccess('Passcode copied'),
      () => toastError('Could not copy'),
    )
  }, [generatedPasscode])

  //--------------------------------------------------------------------------------
  const handleHidePasscode = useCallback(() => {
    setGeneratedPasscode(null)
    toastSuccess('Passcode hidden for safety')
  }, [])

  //--------------------------------------------------------------------------------
  const handleCopyToClipboard = useCallback((title: string, text: string) => {
    void navigator.clipboard.writeText(text).then(
      () => toastSuccess(`${title} copied`),
      () => toastError('Could not copy'),
    )
  }, [])

  //--------------------------------------------------------------------------------
  //TODO: Improve this
  if (loading) return <main><p>Loading…</p></main>
  if (feed === null && feedId !== 'new') return <main><p>Feed not found</p></main>

  //--------------------------------------------------------------------------------
  return (
    <BasicPage
      title={feedId === 'new'
        ? 'New feed'
        : 'Feed: ' + name || 'Edit feed'
      }
      className='FeedEditPage'
      toolbarButtons={<>
        <Button
          label='Feeds'
          icon='chevronLeft'
          href={routes.home(tenantId!)}
        />
        <Button
          label='New feed'
          icon='plus'
          disabled={!canEdit || feedId === 'new'}
          href={routes.feedEdit(tenantId!, 'new')}
        />
        <Button
          variant='primary'
          label='Save'
          icon='save'
          disabled={!canEdit}
          onClick={() => handleSave()}
        />
        <Button
          label={feed?.disabled ? 'Disabled' : 'Enabled'}
          icon={feed?.disabled ? 'regSquare' : 'checkSquare'}
          title={feed?.disabled ? 'Enable feed (make available for viewing)' : 'Disable feed (hide from viewing)'}
          disabled={!canEdit || feedId === 'new'}
          onClick={() => handleSave(feed?.disabled ? 'enable' : 'disable')}
        />
        <Button
          label='Delete'
          icon='trash'
          disabled={!canEdit || feedId === 'new'}
          onClick={handleDelete}
        />
        {feedId !== 'new' && (
          <Button
            label='View feed'
            icon='camera'
            href={routes.feedView(tenantId!, feedId!)}
          />
        )}
      </>}
    >
      {feed?.disabled && (
        <Notice variant='warning' icon='exclamationTriangle'>
          This feed is disabled. It will not store new images.
        </Notice>
      )}

      <div className='FixedBricks'>

        <div className='Cutout Wrapped'>

          <h2>Basics</h2>
          <TextBox
            label='Feed ID'
            readOnly
            width={120}
            value={feed?.id || 'new'}
          />
          <TextBox
            label='Name'
            required
            width={320}
            value={name}
            setValue={setName}
          />
          <TimezoneBox
            label='Timezone'
            tooltip='e.g. America/New_York'
            required
            value={timezone}
            setValue={setTimezone}
          />

          <h2>Transform image</h2>

          <TextBox
            label='Image height'
            suffix='px'
            tooltip={<>
              <p>Resize photo to this height (in pixels). Common values:</p>
              <ul>
                <li>480 - VGA / 480p</li>
                <li>720 - HD / 720p</li>
                <li>1080 - Full HD / 1080p</li>
                <li>1440 - QHD / 2K</li>
                <li>2160 - UHD / 4K</li>
                <li>4320 - 8K</li>
              </ul>
            </>}
            width={150}
            required
            type='number'
            min={100}
            step={1}
            rewriteOnBlur={v => nonzeroInteger(v, 480, 4320)}
            value={imgHeight}
            setValue={setImgHeight}
          />
          <SelectBox<FeedRow['flip']>
            label='Flip'
            tooltip='Mirror-flip the image horizontally (left/right) or vertically (up/down)'
            emptyOptionLabel='No'
            options={[
              { label: 'Horizontal', value: 'H' },
              { label: 'Vertical', value: 'V' },
            ]}
            valueToString={v => v ?? ''}
            valueFromString={s => s === '' ? undefined : s as FeedRow['flip']}
            value={flip ?? ''}
            setValue={setFlip}
          />
          <SelectBox<FeedRow['rotate']>
            label='Rotate'
            tooltip='Rotate the image clockwise (+90°), anti/counter-clockwise (-90°), or 180° (upside-down)'
            emptyOptionLabel='No'
            options={[
              { label: '+90°', value: 'R' },
              { label: '-90°', value: 'L' },
              { label: '180°', value: 'U' },
            ]}
            valueToString={v => v ?? ''}
            valueFromString={s => s === '' ? undefined : s as FeedRow['rotate']}
            value={rotate ?? ''}
            setValue={setRotate}
          />

          <h2>Notes</h2>
          <TextArea
            label='Your private notes (whatever you want)'
            width='30rem'
            autoHeight
            value={notes}
            setValue={setNotes}
          />

        </div>

        <div className='Cutout Wrapped'>
          <h2>Time slots</h2>
          <TextBox
            label='Slot early'
            suffix='s'
            tooltip='Fudge factor. Seconds before a time slot to accept the image. 0 means do not accept images before the time slot opens.'
            width={140}
            type='number'
            min={0}
            step={1}
            rewriteOnBlur={nonzeroInteger}
            value={timeSlotEarly}
            setValue={setTimeSlotEarly}
          />
          <TextBox
            label='Slot late'
            suffix='s'
            width={140}
            type='number'
            min={0}
            step={1}
            rewriteOnBlur={nonzeroInteger}
            tooltip='Fudge factor. Seconds after a time slot to stop waiting for the image. Leave blank to allow up to the next time slot or end of day.'
            value={timeSlotLate}
            setValue={setTimeSlotLate}
          />
          <br />
          <TimeSlots
            value={timeSlots}
            onChange={setTimeSlots}
            disabled={!canEdit}
          />
        </div>

        <div className='Cutout Wrapped'>

          <h2>Photo source</h2>

          <FeedEditPhotoSource
            canEdit={canEdit}
            feedId={feedId}
            tenantId={tenantId}
            feedDbId={feed?.id}
            generatedPasscode={generatedPasscode}
            generatingPasscode={generatingPasscode}
            onGeneratePasscode={handleGeneratePasscode}
            onCopyPasscode={handleCopyPasscode}
            onHidePasscode={handleHidePasscode}
            onCopyToClipboard={handleCopyToClipboard}
          />

        </div>

        <div className='Cutout Wrapped'>

          <h2>Camera security</h2>

          <TextArea
            label='Allowed IP / CIDR ranges'
            tooltip={<>
              <p>
                Filter by camera IP address. One IPv6 CIDR range
                per line. Here's an example of one exact IPv4 address 
                and one subnet defined by the first two octets (43.26.*.*)
                of an IPv4 address:
              </p>
              <p>
                ::ffff:<b style={{ color: 'var(--color-sentiment-negative)' }}>43.26.214.226</b>/128
                <br />::ffff:<b style={{ color: 'var(--color-sentiment-negative)' }}>43.26.</b>0.0/112
              </p>
            </>}
            width='30rem'
            autoHeight
            value={remoteAddressRanges}
            setValue={setRemoteAddressRanges}
          />

        </div>

        <div className='Cutout Wrapped'>

          <h2>Retention policy</h2>

          <FeedEditSchedule
            busyCulling={busyCulling}
            value={schedule}
            setValue={setScheduleFromEditor}
            disabled={!canEdit}
            ingestSlots={ingestSlotsForValidation}
            photosCulled={photosCulled}
            photosRemaining={photosRemaining}
          />

        </div>

      </div>

    </BasicPage>
  )
}


//--------------------------------------------------------------------------------
function nonzeroInteger(text: string, minValue = 0, maxValue = Infinity): string {
  if (!text) return ''
  const intValue = Math.round(Math.max(minValue, Math.min(maxValue, +text)))
  if (intValue <= 0) return ''
  return String(intValue)
}
